/**
 * Pagination Component
 * Based on @uicwebmaster/companies pagination
 */

'use client';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export function Pagination({ currentPage, totalPages, onPageChange }: PaginationProps) {
  if (totalPages <= 1) {
    return null;
  }

  const pages: number[] = [];
  const showEllipsisStart = currentPage > 3;
  const showEllipsisEnd = currentPage < totalPages - 2;

  // Always show first page
  pages.push(1);

  // Add ellipsis or pages before current
  if (showEllipsisStart) {
    pages.push(-1); // Represents ellipsis
  } else {
    for (let i = 2; i < currentPage; i++) {
      pages.push(i);
    }
  }

  // Add current page and neighbors
  if (currentPage > 1 && currentPage < totalPages) {
    if (currentPage - 1 > 1 && !showEllipsisStart) {
      pages.push(currentPage - 1);
    } else if (currentPage > 2) {
      pages.push(currentPage - 1);
    }
    pages.push(currentPage);
    if (currentPage + 1 < totalPages && !showEllipsisEnd) {
      pages.push(currentPage + 1);
    } else if (currentPage < totalPages - 1) {
      pages.push(currentPage + 1);
    }
  }

  // Add ellipsis or pages after current
  if (showEllipsisEnd) {
    pages.push(-2); // Represents ellipsis
  } else {
    for (let i = currentPage + 1; i < totalPages; i++) {
      if (!pages.includes(i)) {
        pages.push(i);
      }
    }
  }

  // Always show last page
  if (!pages.includes(totalPages)) {
    pages.push(totalPages);
  }

  return (
    <nav
      role="navigation"
      aria-label="Pagination"
      className="flex items-center justify-center gap-1"
    >
      <button
        type="button"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="rounded-md border border-input bg-background px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
        aria-label="Go to previous page"
      >
        Previous
      </button>

      {pages.map((page, index) => {
        if (page === -1 || page === -2) {
          return (
            <span
              key={`ellipsis-${index}`}
              className="px-3 py-2 text-sm text-muted-foreground"
              aria-hidden="true"
            >
              …
            </span>
          );
        }

        return (
          <button
            key={page}
            type="button"
            onClick={() => onPageChange(page)}
            className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
              page === currentPage
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-input bg-background hover:bg-accent hover:text-accent-foreground'
            }`}
            aria-label={`Go to page ${page}`}
            aria-current={page === currentPage ? 'page' : undefined}
          >
            {page}
          </button>
        );
      })}

      <button
        type="button"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="rounded-md border border-input bg-background px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
        aria-label="Go to next page"
      >
        Next
      </button>
    </nav>
  );
}

